import React from 'react';
import { 
  Bot, 
  BarChart3, 
  MessageSquare, 
  Settings, 
  User, 
  Phone, 
  FileSearch, 
  DollarSign, 
  Webhook, 
  Activity, 
  Code, 
  PhoneCall, 
  Shield, 
  BookOpen, 
  Sparkles,
  ArrowRight,
  PlugZap
} from 'lucide-react';

const Workspace = ({ onSectionChange }) => {
  const navigationItems = [
    { 
      id: 'overview', 
      title: 'Overview', 
      icon: BarChart3,
      description: 'Dashboard overview and analytics',
      color: 'from-blue-500/20 to-blue-600/10',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400',
      category: 'Analytics'
    },
    { 
      id: 'agents', 
      title: 'Voice Agents', 
      icon: Bot,
      description: 'Manage and configure AI voice agents',
      color: 'from-purple-500/20 to-purple-600/10',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-400',
      category: 'Core'
    },
    { 
      id: 'conversations', 
      title: 'Conversations', 
      icon: MessageSquare,
      description: 'View and manage conversation sessions',
      color: 'from-green-500/20 to-green-600/10',
      borderColor: 'border-green-500/30',
      iconColor: 'text-green-400',
      category: 'Core'
    },
    { 
      id: 'session-analysis', 
      title: 'Session Analysis', 
      icon: FileSearch,
      description: 'Analyze call sessions and transcripts',
      color: 'from-cyan-500/20 to-cyan-600/10',
      borderColor: 'border-cyan-500/30',
      iconColor: 'text-cyan-400',
      category: 'Analytics'
    },
    { 
      id: 'phone-numbers', 
      title: 'Phone Numbers', 
      icon: Phone,
      description: 'Manage phone numbers and telephony',
      color: 'from-indigo-500/20 to-indigo-600/10',
      borderColor: 'border-indigo-500/30',
      iconColor: 'text-indigo-400',
      category: 'Telephony'
    },
    { 
      id: 'outbound-api', 
      title: 'Outbound API', 
      icon: PhoneCall,
      description: 'Make outbound calls via API',
      color: 'from-orange-500/20 to-orange-600/10',
      borderColor: 'border-orange-500/30',
      iconColor: 'text-orange-400',
      category: 'Telephony'
    },
    { 
      id: 'pii-preview', 
      title: 'PII Preview', 
      icon: Shield,
      description: 'Preview and manage PII redaction',
      color: 'from-red-500/20 to-red-600/10',
      borderColor: 'border-red-500/30',
      iconColor: 'text-red-400',
      category: 'Security'
    },
    { 
      id: 'knowledge-base', 
      title: 'Knowledge Base', 
      icon: BookOpen,
      description: 'Manage knowledge base documents',
      color: 'from-emerald-500/20 to-emerald-600/10',
      borderColor: 'border-emerald-500/30',
      iconColor: 'text-emerald-400',
      category: 'Content'
    },
    { 
      id: 'costs', 
      title: 'Cost & Pricing', 
      icon: DollarSign,
      description: 'View costs and pricing information',
      color: 'from-yellow-500/20 to-yellow-600/10',
      borderColor: 'border-yellow-500/30',
      iconColor: 'text-yellow-400',
      category: 'Billing'
    },
    { 
      id: 'webhooks', 
      title: 'Webhooks', 
      icon: Webhook,
      description: 'Configure webhook endpoints',
      color: 'from-pink-500/20 to-pink-600/10',
      borderColor: 'border-pink-500/30',
      iconColor: 'text-pink-400',
      category: 'Integrations'
    },
    { 
      id: 'webhook-events', 
      title: 'Webhook Events', 
      icon: Activity,
      description: 'Monitor webhook event logs',
      color: 'from-violet-500/20 to-violet-600/10',
      borderColor: 'border-violet-500/30',
      iconColor: 'text-violet-400',
      category: 'Integrations'
    },
    { 
      id: 'custom-functions', 
      title: 'Custom Functions', 
      icon: Code,
      description: 'Create and manage custom functions',
      color: 'from-teal-500/20 to-teal-600/10',
      borderColor: 'border-teal-500/30',
      iconColor: 'text-teal-400',
      category: 'Integrations'
    },
    { 
      id: 'api-injection', 
      title: 'API Injection', 
      icon: Sparkles,
      description: 'Inject data into active sessions',
      color: 'from-rose-500/20 to-rose-600/10',
      borderColor: 'border-rose-500/30',
      iconColor: 'text-rose-400',
      category: 'Integrations'
    },
    { 
      id: 'integrations', 
      title: 'Integrations', 
      icon: PlugZap,
      description: 'Connect with third-party platforms and tools',
      color: 'from-amber-500/20 to-amber-600/10',
      borderColor: 'border-amber-500/30',
      iconColor: 'text-amber-400',
      category: 'Integrations'
    },
    { 
      id: 'profile', 
      title: 'Profile', 
      icon: User,
      description: 'Manage your user profile',
      color: 'from-gray-500/20 to-gray-600/10',
      borderColor: 'border-gray-500/30',
      iconColor: 'text-gray-400',
      category: 'Settings'
    },
  ];

  // Group items by category
  const groupedItems = navigationItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const categories = Object.keys(groupedItems);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground mb-1 flex items-center gap-2">
          <Settings className="w-6 h-6 text-primary" />
          Workspace
        </h1>
        <p className="text-sm text-muted-foreground">Access all features and tools from your workspace</p>
      </div>

      {/* Grid Layout */}
      <div className="flex-1 flex flex-col min-h-0 space-y-5 overflow-y-auto">
        {categories.map((category) => (
          <div key={category}>
            <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <div className="w-1 h-4 bg-gradient-button rounded-full"></div>
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupedItems[category].map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSectionChange(item.id)}
                    className={`
                      group relative overflow-hidden
                      rounded-lg border ${item.borderColor} 
                      bg-gradient-to-br ${item.color}
                      bg-card/80 backdrop-blur-sm
                      p-4
                      hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10
                      transition-all duration-300
                      text-left
                      focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
                    `}
                  >
                    {/* Icon */}
                    <div className="flex items-start justify-between mb-3">
                      <div className={`
                        w-10 h-10 rounded-lg 
                        bg-gradient-button 
                        flex items-center justify-center 
                        shadow-md
                        group-hover:scale-110
                        transition-transform duration-300
                      `}>
                        <Icon className={`w-5 h-5 text-primary-foreground`} />
                      </div>
                      <ArrowRight className={`
                        w-4 h-4 
                        ${item.iconColor}
                        opacity-0 group-hover:opacity-100 
                        group-hover:translate-x-1
                        transition-all duration-300
                      `} />
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-1.5 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-snug">
                        {item.description}
                      </p>
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Workspace;

